// Shared RRULE expansion engine for Edge Functions (C4).
//
// Computes occurrence start instants for a recurring event from its master
// start instant and a recurrence rule. Scope is the subset of RFC 5545 the app
// actually authors (see RecurrenceRule in the frontend event types):
//   FREQ = daily | weekly | monthly | yearly, INTERVAL, BYDAY (weekly),
//   BYMONTHDAY (monthly), BYMONTH (yearly), COUNT, UNTIL, EXDATE.
//
// Timezone note: all arithmetic is done in UTC. This keeps expansion
// deterministic and DST-safe for instants stored as `timestamptz`. Wall-clock
// "same local time across a DST boundary" semantics are intentionally NOT
// modelled (documented limitation).
//
// IMPORTED BY THE CLIENT AS OF WAVE 8.
// src/features/family-calendar/utils/recurrence.utils.ts imports this module
// directly. tsc follows imports regardless of tsconfig's `include: ["src/**/*"]`,
// so from now on the APP's strict flags — noUncheckedIndexedAccess,
// exactOptionalPropertyTypes, noImplicitReturns — police this file on every
// `npm run build`. An edit here can break the CLIENT build, not just Deno.
//
// This module is importable only because it is a ZERO-IMPORT LEAF. Any Edge
// module that imports a sibling with a `.ts` specifier, or over `jsr:`/`https:`,
// will NOT resolve under vite. Do not generalise this precedent.

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type DayOfWeek = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

export interface RecurrenceRule {
  readonly frequency: RecurrenceFrequency;
  readonly interval: number;
  readonly byDay?: readonly DayOfWeek[];
  readonly byMonthDay?: number;
  readonly byMonth?: number;
  readonly until?: string;
  readonly count?: number;
  readonly exDates?: readonly string[];
}

/** Hard safety cap so a malformed rule can never loop unbounded. */
const MAX_OCCURRENCES = 1000;

/** JS getUTCDay() (0=Sun..6=Sat) → RRULE day code. */
const DOW_CODES: readonly DayOfWeek[] = [
  'SU',
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
];

function dayCode(date: Date): DayOfWeek {
  // The `?? 'SU'` branch is UNREACHABLE, and that claim is load-bearing rather
  // than decorative: getUTCDay() returns 0-6 for any VALID date and DOW_CODES
  // has exactly 7 entries (see the table above), so the only way to reach the
  // fallback is an Invalid Date, whose getUTCDay() is NaN. Every caller is
  // downstream of expandOccurrences' opening refusal of an unparseable bound
  // and of the weekly branch's NaN-safe loop test, so an Invalid Date cannot
  // arrive here. The branch exists only to satisfy the app tsconfig's
  // `noUncheckedIndexedAccess`, which now polices this file because src/
  // imports it. Do not "simplify" it away — that breaks the CLIENT build, not
  // just Deno. And if you ever remove the refusal, this stops being a type
  // formality and starts silently relabelling every Invalid Date as Sunday.
  return DOW_CODES[date.getUTCDay()] ?? 'SU';
}

/**
 * Truncate an instant to its UTC calendar day (midnight UTC).
 *
 * Exported as of Wave 8: the client's exDate writer and the expander's prune
 * both need this exact truncation, and a hand-rolled second copy is how
 * UTC-versus-local skew gets reintroduced.
 */
export function toUtcDayStart(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
}

/** Apply the master's UTC time-of-day to a target UTC calendar day. */
function withTimeOfDay(day: Date, source: Date): Date {
  return new Date(
    Date.UTC(
      day.getUTCFullYear(),
      day.getUTCMonth(),
      day.getUTCDate(),
      source.getUTCHours(),
      source.getUTCMinutes(),
      source.getUTCSeconds(),
      source.getUTCMilliseconds()
    )
  );
}

function addUtcDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

/**
 * Build a UTC instant for (year, monthIndex, day) at the master's time of day,
 * returning `null` if the day overflows the month (e.g. Feb 31) so the caller
 * can skip it rather than silently rolling into the next month.
 */
function safeMonthlyInstant(
  year: number,
  monthIndex: number,
  day: number,
  source: Date
): Date | null {
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  if (day > daysInMonth) {
    return null;
  }
  return new Date(
    Date.UTC(
      year,
      monthIndex,
      day,
      source.getUTCHours(),
      source.getUTCMinutes(),
      source.getUTCSeconds(),
      source.getUTCMilliseconds()
    )
  );
}

export interface ExpandOptions {
  /** Master event start instant. */
  readonly start: Date;
  /** Recurrence rule. */
  readonly rule: RecurrenceRule;
  /** Inclusive upper bound — no occurrence after this instant is generated. */
  readonly horizon: Date;
}

/**
 * Generate occurrence start instants AFTER the master start, up to the horizon.
 *
 * The master's own start is treated as occurrence #1 and is NOT included in the
 * returned list (it already exists as the authored row). COUNT therefore caps
 * the returned length at `count - 1`. EXDATE values (compared by UTC day) are
 * excluded.
 *
 * An unparseable `start`, `horizon` or `rule.until` yields an EMPTY list. See
 * the refusal below for why that is the only safe answer.
 */
export function expandOccurrences(options: ExpandOptions): Date[] {
  const { start, rule, horizon } = options;

  // REFUSE an unparseable bound instead of expanding from one.
  //
  // Every loop below terminates on a comparison against `upperBound`, and
  // every comparison with NaN is FALSE — so an Invalid Date does not stop a
  // loop, it disables the stop. Measured on the weekly branch: an Invalid Date
  // `start` with `byDay` omitted produced MAX_OCCURRENCES x 7 Invalid Dates,
  // because the fallback day set was built from `dayCode(start)` (undefined)
  // and the per-day lookup returned undefined too, so every day matched. An
  // Invalid Date `horizon` does the same thing to a VALID start.
  //
  // Returning [] is a refusal, not a silent success: the caller renders/writes
  // nothing for a series it cannot compute, which is strictly better than
  // thousands of `Invalid Date` rows. It also makes `dayCode`'s `?? 'SU'`
  // genuinely unreachable, which is what that comment claims.
  if (Number.isNaN(start.getTime()) || Number.isNaN(horizon.getTime())) {
    return [];
  }

  const interval = Math.max(1, Math.floor(rule.interval || 1));
  const until = rule.until ? new Date(rule.until) : null;
  // An unparseable UNTIL is refused rather than IGNORED. Ignoring it silently
  // widens the series past the instant the user asked it to stop at, and the
  // cron would then materialise rows for a rule nobody authored; an empty
  // expansion is the visible failure.
  if (until !== null && Number.isNaN(until.getTime())) {
    return [];
  }
  const upperBound =
    until && until.getTime() < horizon.getTime() ? until : horizon;

  // Remaining slots after the master occurrence.
  const remaining =
    typeof rule.count === 'number' && rule.count > 0
      ? rule.count - 1
      : Number.POSITIVE_INFINITY;

  const exDays = new Set(
    (rule.exDates ?? []).map((iso) => toUtcDayStart(new Date(iso)).getTime())
  );

  const results: Date[] = [];
  const accept = (instant: Date): boolean => {
    if (instant.getTime() <= start.getTime()) return true; // not yet past master
    if (instant.getTime() > upperBound.getTime()) return false;
    if (exDays.has(toUtcDayStart(instant).getTime())) return true;
    results.push(instant);
    return true;
  };

  switch (rule.frequency) {
    case 'daily': {
      let cursor = addUtcDays(start, interval);
      let guard = 0;
      while (
        cursor.getTime() <= upperBound.getTime() &&
        results.length < remaining &&
        guard < MAX_OCCURRENCES
      ) {
        accept(cursor);
        cursor = addUtcDays(cursor, interval);
        guard += 1;
      }
      break;
    }

    case 'weekly': {
      const days =
        rule.byDay && rule.byDay.length > 0 ? rule.byDay : [dayCode(start)];
      const daySet = new Set(days);
      // Anchor weeks to the master's week (Monday-start).
      const masterDay = toUtcDayStart(start);
      const mondayOffset = (masterDay.getUTCDay() + 6) % 7; // 0 = Monday
      const weekAnchor = addUtcDays(masterDay, -mondayOffset);

      let weekIndex = 0;
      let guard = 0;
      while (results.length < remaining && guard < MAX_OCCURRENCES) {
        const weekStart = addUtcDays(weekAnchor, weekIndex * 7);
        // `!(a <= b)`, not `a > b`. Identical for two valid instants; the
        // difference is NaN. A start close enough to the ±8.64e15 ms Date
        // range limit makes `weekStart` overflow to an Invalid Date, and
        // `NaN > x` is FALSE — the `>` form would keep looping and hand an
        // Invalid Date to `dayCode`. This is the second half of what makes
        // `dayCode`'s `?? 'SU'` unreachable; the first is the refusal at the
        // top of this function.
        if (!(weekStart.getTime() <= upperBound.getTime())) break;

        if (weekIndex % interval === 0) {
          for (let d = 0; d < 7; d += 1) {
            const day = addUtcDays(weekStart, d);
            if (!daySet.has(dayCode(day))) continue;
            const instant = withTimeOfDay(day, start);
            if (instant.getTime() > upperBound.getTime()) break;
            if (!accept(instant)) break;
            if (results.length >= remaining) break;
          }
        }
        weekIndex += 1;
        guard += 1;
      }
      break;
    }

    case 'monthly': {
      const targetDay = rule.byMonthDay ?? start.getUTCDate();
      let step = interval;
      let guard = 0;
      while (results.length < remaining && guard < MAX_OCCURRENCES) {
        const year = start.getUTCFullYear();
        const monthIndex = start.getUTCMonth() + step;
        const instant = safeMonthlyInstant(year, monthIndex, targetDay, start);
        if (instant && instant.getTime() > upperBound.getTime()) break;
        if (instant) accept(instant);
        // Stop if even the calendar position has passed the bound.
        const monthFloor = new Date(Date.UTC(year, monthIndex, 1));
        if (monthFloor.getTime() > upperBound.getTime()) break;
        step += interval;
        guard += 1;
      }
      break;
    }

    case 'yearly': {
      const monthIndex =
        typeof rule.byMonth === 'number'
          ? rule.byMonth - 1
          : start.getUTCMonth();
      const targetDay = start.getUTCDate();
      let step = interval;
      let guard = 0;
      while (results.length < remaining && guard < MAX_OCCURRENCES) {
        const year = start.getUTCFullYear() + step;
        const instant = safeMonthlyInstant(year, monthIndex, targetDay, start);
        if (instant && instant.getTime() > upperBound.getTime()) break;
        if (instant) accept(instant);
        const yearFloor = new Date(Date.UTC(year, monthIndex, 1));
        if (yearFloor.getTime() > upperBound.getTime()) break;
        step += interval;
        guard += 1;
      }
      break;
    }
  }

  return results.slice(
    0,
    Number.isFinite(remaining) ? (remaining as number) : results.length
  );
}
